import DOMPurify from "dompurify";
import { AppSettingsService } from "@services";

export default class CommonService {
  static tempTextArea = document.createElement("textarea");
  static tempDiv = document.createElement("div");

  static htmlDecode(input) {
    if (!input) return "";

    if (input.includes("&gt") || input.includes("&lt")) {
      CommonService.tempTextArea.innerHTML = input;
      return CommonService.tempTextArea.value;
    } else {
      return input;
    }
  }

  static encodeAngleBrackets(input) {
    // Match valid HTML tags
    const validTagRegex = /<\/?[a-zA-Z][\w\-]*(\s+[a-zA-Z_:][\w\-.:]*\s*=\s*"[^"]*")*\s*\/?>/g;

    // Temporarily store valid tags
    const validTags = [];
    const placeholder = "___HTML_TAG___";

    // Replace valid tags with placeholders
    const temp = input.replace(validTagRegex, match => {
      validTags.push(match);
      return placeholder;
    });

    // Encode remaining angle brackets
    const encoded = temp
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

    // Restore valid tags
    let final = encoded;
    validTags.forEach(tag => {
      final = final.replace(placeholder, tag);
    });

    return final;
  }

  static parseJwt(token) {
    var base64Url = token.split(".")[1];
    var base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    var jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );

    return JSON.parse(jsonPayload);
  }

  static replaceDatePlaceholders(inputString) {
    const currentDate = new Date();
    const year = currentDate.getFullYear().toString();
    const month = (currentDate.getMonth() + 1).toString().padStart(2, "0");
    const day = currentDate.getDate().toString().padStart(2, "0");
    const monthName = currentDate.toLocaleString("default", { month: "long" });
    const replacedString = inputString
      .replace(/\[MM\/DD\/YYYY\]/gi, month + "/" + day + "/" + year)
      .replace(/\[mm\/dd\/yyyy\]/g, month + "/" + day + "/" + year)
      .replace(/\[mm\/yy\]/g, month + "/" + year.slice(-2))
      .replace(/\[MM\/YY\]/g, month + "/" + year.slice(-2))
      .replace(/\[mm\/yyyy\]/g, month + "/" + year)
      .replace(/\[MM\/YYYY\]/g, month + "/" + year)
      .replace(/\[month day, year\]/gi, monthName + " " + day + ", " + year)
      .replace(/\[month year\]/gi, monthName + " " + year);
    return replacedString;
  }
  static replacePlaceholdersWithResults(html, result) {
    if (!html || typeof html !== "string" || !result || typeof result !== "object") {
      return html;
    }
    var resultMap = {};
    for (var key in result) {
      if (result.hasOwnProperty(key)) {
        resultMap[key.toLowerCase()] = result[key];
      }
    }
    return html.replace(/\[([^\[\]]+)\]/gi, function (match, placeholder) {
      var lowerKey = placeholder.toLowerCase();
      return resultMap.hasOwnProperty(lowerKey)
        ? resultMap[lowerKey]
        : match;
    });
  }

  static isEmpty(value) {
    return value == null || value === "";
  }

  // Check if the input contains any HTML tags
  static isHtmlText(input) {
    if (!input || !(typeof input === "string" || input instanceof String)) return false;
    const decodedInput = CommonService.htmlDecode(input);
    return /<\/?[a-z][\s\S]*>/i.test(decodedInput);
  }

  // Extract plain text from HTML while minimizing DOM manipulations
  static extractTextFromHtmlText(input) {
    if (!input) return "";

    if (!CommonService.isHtmlText(input)) return input;

    const decodedInput = CommonService.htmlDecode(input);
    CommonService.tempDiv.innerHTML = decodedInput;
    return CommonService.tempDiv.textContent.trim();
  }

  static isUrl(input) {
    return input?.startsWith("https://") || input?.startsWith("http://");
  }

  static getHrefFromHtml(htmlString) {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlString;
    const anchor = tempDiv.querySelector("a");
    return anchor ? anchor.getAttribute("href") : null;
  }

  static hideCfHeaderStatement = (isResultShow, dynamicHtmlRef) => {
    const container = dynamicHtmlRef.current;
    if (container) {
      const element = container.querySelector("#cfHeaderStatement");
      if (element) {
        element.style.display = isResultShow ? "none" : "block"; // Hide the element
      }
    }
  };

  static objectHasProperty(obj, prop) {
    if (obj === null || obj === undefined) {
      return false;
    }

    if (Object.hasOwn) {
      return Object.hasOwn(obj, prop);
    } else {
      return Object.prototype.hasOwnProperty.call(obj, prop);
    }
  }

  static sanitizeHTML(value) {
    return DOMPurify.sanitize(value, { USE_PROFILES: { html: true }, ADD_ATTR: ["target"] });
  }

  static getHtmlFileUrl(htmlFile, clientId) {
    if (!clientId || !htmlFile) {
      return "";
    }
    const cdnUrl = AppSettingsService.getInstance().getCDNUrl();
    return cdnUrl + "html-files/" + clientId + "/" + htmlFile;
  }

  static getIcon(iconName, clientId, defaultIcon) {
    if (!iconName) {
      return defaultIcon;
    }
    if (iconName === "No Icon") {
      return null;
    }
    const cdnUrl = AppSettingsService.getInstance().getCDNUrl();
    return cdnUrl + "icons/" + clientId + "/" + iconName;
  }

  static getSafeAlphaNumericText(text, replaceWith = "-") {
    return text?.trim()?.toLowerCase().replace(/[^a-z0-9]+/g, replaceWith);
  }

  static getDynamicHtmlForCoverageStatus = (inputHtml, coverageStatus) => {
    if (!inputHtml) return "";
    if (!CommonService.isHtmlText(inputHtml)) return inputHtml;

    CommonService.tempDiv.innerHTML = CommonService.htmlDecode(inputHtml);
    const conditionalEls = CommonService.tempDiv.querySelectorAll("[coverage-statuses]");
    
    if (!conditionalEls || conditionalEls.length === 0) return inputHtml;

    conditionalEls.forEach(el => {
      const statuses = el
          .getAttribute("coverage-statuses")
          .split(",")
          .map(s => s.trim().toLowerCase());

      // Show only if it matches the current status
      if (statuses.includes(coverageStatus?.toLowerCase())) {
          el.style.display = "block";
      } else {
          el.style.display = "none";
      }
    });
    return CommonService.tempDiv.innerHTML;
  };  

  static convertSafeJoinWithCommasToArray = (arr) => {
        // Match either quoted strings or unquoted words
        const regex = /"([^"]*)"|([^,]+)/g;

        const result = [];
        let match;
        while ((match = regex.exec(arr)) !== null) {
            // match[1] is the quoted value, match[2] is the unquoted value
            result.push(match[1] || match[2]);
        }

        return result;
    };
}
